import {
  forwardRef,
  type MouseEvent,
  useCallback,
  useMemo,
  useState
} from 'react';
import styled, { css, useTheme } from 'styled-components';
import { Icon } from '@elfsight/lib-icon';
import { COMMON_MOBILE_BREAKPOINT } from '@elfsight/lib-utils';
import {
  Popup,
  type PopupProps,
  Tooltip,
  zIndexLayer
} from '@elfsight/widget-modal';
import { Button } from '@elfsight/widget-ui';
import { useIsMobile } from '@elfsight/widget-utils';
import Info from '../../icons/info.svg?react';

type ModeInfoIconProps = {
  label: string;
  description: string;
  active: boolean;
  className?: string;
};

export const ModeInfoIcon = forwardRef<HTMLDivElement, ModeInfoIconProps>(
  ({ label, description, active, className }, forwardingRef) => {
    const theme = useTheme();
    const isMobile = useIsMobile();
    const [show, setShow] = useState(false);

    const onCloseRequest: PopupProps['onCloseRequest'] = (event) => {
      // prevent applying mode settings when calling onCloseRequest
      event?.stopPropagation();

      setShow(false);
    };

    const handleIconClick = useCallback(
      (e: MouseEvent<HTMLDivElement>) => {
        if (!isMobile) {
          return;
        }

        e.stopPropagation();
        setShow(true);
      },
      [isMobile]
    );

    const tooltipContent = useMemo(
      () => <TooltipContent>{description}</TooltipContent>,
      [description]
    );

    return (
      <>
        <Tooltip title={tooltipContent} disabled={isMobile}>
          <InfoIconContainer
            $active={active}
            ref={forwardingRef}
            tabIndex={0}
            className={className}
            component={Info}
            size={24}
            fill={
              active ? theme.modeInfoIcon.activeColor : theme.modeInfoIcon.color
            }
            onClick={handleIconClick}
          />
        </Tooltip>

        {isMobile && (
          <Popup
            show={show}
            zIndex={zIndexLayer.modal + 1}
            backdropColor="rgba(17, 17, 17, 0.7)"
            margin={40}
            onCloseRequest={onCloseRequest}
            closeOnOutsideClick
            fixed
          >
            <PopupContainer>
              <PopupTitle>{label}</PopupTitle>
              <PopupText>{description}</PopupText>
              <Button
                color={theme.modeInfoIcon.popupButtonBackgroundColor}
                fontColor={theme.modeInfoIcon.popupButtonColor}
                horizontalPadding={22}
                verticalPadding={10}
                font={{
                  fontSize: 15,
                  fontWeight: 700
                }}
                lineHeight={1.333}
                borderRadius={44}
                onClick={onCloseRequest}
              >
                OK
              </Button>
            </PopupContainer>
          </Popup>
        )}
      </>
    );
  }
);

const TooltipContent = styled.div`
  font-size: 15px;
  line-height: 20px;
`;

const InfoIconContainer = styled(Icon)<{
  $active: boolean;
}>`
  ${({ $active }) => css`
    opacity: ${$active ? 1 : 0};
    transition: opacity 0.15s;

    &:focus {
      outline: none;
    }

    @media (max-width: ${COMMON_MOBILE_BREAKPOINT}px) {
      opacity: 1;
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `}
`;

const PopupContainer = styled.div`
  ${({ theme }) => css`
    padding: 16px;
    border-radius: 20px;
    text-align: center;
    background-color: ${theme.modeInfoIcon.popupBackgroundColor};
    color: ${theme.modeInfoIcon.popupTextColor};
  `}
`;

const PopupTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
`;

const PopupText = styled.div`
  font-size: 16px;
  line-height: 22px;
  margin-bottom: 12px;
`;
